// src/pages/Register.jsx
import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google'
import { useAuth } from '../context/AuthContext'
import { register, googleLogin, getProfile } from '../api/services'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID

// -- Error normalisation -----------------------------------------------
const extractError = (err) => {
    const data = err.response?.data
    const status = err.response?.status
    const detail = data?.detail

    if (status === 409 || status === 400) {
        return typeof detail === 'string' ? detail : 'An account with this email already exists.'
    }
    if (Array.isArray(detail)) {
        return detail.map((e) => e.msg.replace('Value error, ', '')).join('. ')
    }
    if (typeof detail === 'string') return detail
    if (!err.response) return 'Unable to reach the server. Please check your connection.'
    return 'Registration failed. Please try again.'
}

// -- Icons -------------------------------------------------------------
function EyeIcon({ visible }) {
    return visible ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
        </svg>
    ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
    )
}

// -- GMC-Branded Google Icon (dark theme version) --------------------------
function GoogleIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M24 9.5C27.6 9.5 30.8 10.8 33.3 13.1L40.5 6.1C36.2 2.3 30.5 0 24 0C14.8 0 7.1 4.8 3.3 11.8L9.9 17.1C12.3 12.5 17.5 9.5 24 9.5Z" fill="#EA4335" />
            <path d="M46.5 24.5C46.5 22.9 46.4 21.4 46.2 20H24V29.4H36.7C35.9 33.8 33.6 37.7 30.1 40.3L36.7 45.7C43.3 40.3 46.5 33.1 46.5 24.5Z" fill="#4285F4" />
            <path d="M9.9 30.9C9.1 29.1 8.6 27.2 8.6 25C8.6 22.8 9.1 20.9 9.9 19.1L3.3 13.8C1.2 17.1 0 21.2 0 25C0 28.8 1.2 32.9 3.3 36.2L9.9 30.9Z" fill="#FBBC05" />
            <path d="M24 48C30.5 48 36.2 46.3 40.7 43.6L34.1 38.2C31.6 40.1 28.3 41.5 24 41.5C17.5 41.5 12.3 38.5 9.9 33.9L3.3 39.2C7.1 46.2 14.8 48 24 48Z" fill="#34A853" />
        </svg>
    )
}

// -- High-End Floating label input -------------------------------------
function FloatingInput({
    label, id, type, value, onChange, placeholder,
    showToggle, onToggle, visible, rightAccessory,
    minLength, borderClass, disabled,
}) {
    const [focused, setFocused] = useState(false)
    const isFilled = value && value.length > 0
    const isActive = focused || isFilled

    const defaultBorder = focused
        ? 'border-brand-amber/40 focus:ring-brand-amber/10'
        : 'border-white/[0.06] hover:border-white/[0.12]'

    const border = borderClass || `${defaultBorder} focus:ring-4`

    return (
        <div className="relative w-full mb-5 font-body">
            <label
                htmlFor={id}
                className={
                    'absolute left-4 pointer-events-none transition-all duration-200 font-sans font-medium tracking-wide ' +
                    (isActive
                        ? 'top-2.5 text-[10px] text-slate-500 uppercase tracking-widest'
                        : 'top-1/2 -translate-y-1/2 text-sm text-slate-400')
                }
            >
                {label}
            </label>
            <input
                id={id}
                name={id}
                type={type}
                value={value}
                onChange={onChange}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder={focused ? placeholder : ''}
                minLength={minLength}
                disabled={disabled}
                className={
                    'w-full bg-white/[0.02] focus:bg-white/[0.04] text-white border rounded-lg px-4 pt-6 pb-2 text-sm font-light placeholder-slate-600 ' +
                    'focus:outline-none transition-all duration-200 shadow-[inset_0_1px_2px_rgba(255,255,255,0.01)] ' +
                    'disabled:opacity-50 disabled:cursor-not-allowed ' +
                    border
                }
                required
            />
            {showToggle && isFilled && (
                <button
                    type="button"
                    onClick={onToggle}
                    tabIndex={-1}
                    aria-label={visible ? 'Hide password' : 'Show password'}
                    className="absolute right-4 top-[23px] text-slate-500 hover:text-slate-300 focus:outline-none transition-colors"
                >
                    <EyeIcon visible={visible} />
                </button>
            )}
            {rightAccessory && !showToggle && (
                <div className="absolute right-4 top-[23px]">{rightAccessory}</div>
            )}
            {rightAccessory && showToggle && isFilled && (
                <div className="absolute right-10 top-[23px]">{rightAccessory}</div>
            )}
        </div>
    )
}

// -- Brand Left Panel Redesign -----------------------------------------
const FEATURES = [
    'Legal exposure detection models',
    'Passive-aggressive escalation scoring',
    'Tone / Register alignment parameters',
    'State-isolated mitigation engine',
]

function BrandPanel({ animateIn }) {
    const show = animateIn ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'

    return (
        <div className="lg:w-[44%] bg-transparent flex-shrink-0 relative flex flex-col justify-between p-8 lg:p-16 text-white min-h-[260px] lg:min-h-screen border-b lg:border-b-0 lg:border-r border-white/[0.04]">
            <div className="hidden lg:block h-10" />

            <div className="my-auto max-w-sm relative z-10">
                <p className={'text-brand-amber text-[10px] font-medium tracking-[0.25em] uppercase mb-4 transition-all duration-700 ease-out ' + show}
                    style={{ transitionDelay: '100ms' }}>
                    Professional Risk Protocol
                </p>
                <h2 className={'font-display font-bold text-3xl lg:text-4xl tracking-tight leading-[1.15] mb-5 transition-all duration-700 ease-out ' + show}
                    style={{ transitionDelay: '200ms' }}>
                    Mitigate communications risk at the runtime layer.
                </h2>
                <p className={'font-body text-sm font-light text-slate-400 leading-relaxed mb-8 transition-all duration-700 ease-out ' + show}
                    style={{ transitionDelay: '300ms' }}>
                    Verify message structural posture, catch trailing liabilities, and maintain absolute structural clarity across enterprise endpoints.
                </p>
                <div className={'space-y-3 transition-all duration-700 ease-out ' + show}
                    style={{ transitionDelay: '350ms' }}>
                    {FEATURES.map((f) => (
                        <div key={f} className="flex items-center gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-white/[0.12] border border-white/[0.2] flex-shrink-0" />
                            <span className="font-body text-sm font-light text-slate-300">
                                {f}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className={'relative z-10 flex items-center gap-2.5 transition-all duration-700 ease-out ' + show}
                style={{ transitionDelay: '400ms' }}>
                <div className="w-5 h-5 rounded-md bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                        stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                </div>
                <span className="text-slate-500 text-xs font-light">
                    Stateless compilation loop. Zero long-term retention.
                </span>
            </div>
        </div>
    )
}

// -- Main Component Redesign -------------------------------------------
export default function Register() {
    const { loginWithToken } = useAuth()
    const navigate = useNavigate()

    const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '' })
    const [confirm, setConfirm] = useState('')
    const [showPass, setShowPass] = useState(false)
    const [showConf, setShowConf] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [animateIn, setAnimateIn] = useState(false)

    const [matchFeedback, setMatchFeedback] = useState(null)
    const feedbackTimer = useRef(null)
    const isMounted = useRef(true)

    useEffect(() => {
        const t = setTimeout(() => setAnimateIn(true), 50)
        return () => {
            clearTimeout(t)
            clearTimeout(feedbackTimer.current)
            isMounted.current = false
        }
    }, [])

    const handleChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }))

    const handlePasswordChange = (e) => {
        const val = e.target.value
        setForm((f) => ({ ...f, password: val }))
        if (confirm.length > 0) {
            clearTimeout(feedbackTimer.current)
            setMatchFeedback(null)
            feedbackTimer.current = setTimeout(() => {
                if (isMounted.current) setMatchFeedback(confirm === val)
            }, 800)
        }
    }

    const handleConfirmChange = (e) => {
        const val = e.target.value
        setConfirm(val)
        setMatchFeedback(null)
        clearTimeout(feedbackTimer.current)
        if (val.length > 0) {
            feedbackTimer.current = setTimeout(() => {
                if (isMounted.current) setMatchFeedback(val === form.password)
            }, 900)
        }
    }

    const triggerGoogleSignIn = () => {
        const googleContainer = document.querySelector('#google-signin-container')
        if (googleContainer) {
            const button = googleContainer.querySelector('button') ||
                googleContainer.querySelector('[role="button"]')
            if (button) {
                button.click()
            }
        }
    }

    const handleGoogleSuccess = async (credentialResponse) => {
        setLoading(true)
        setError(null)
        try {
            const res = await googleLogin(credentialResponse.credential)
            const token = res?.data?.access_token

            if (!token) {
                setError('Google registration failed. No access token provided.')
                return
            }

            localStorage.setItem('gmc_token', token)

            let profileData = null
            try {
                const profileRes = await getProfile()
                profileData = profileRes.data
            } catch (profileErr) {
                console.warn('Post-auth profile fetch failed (non-fatal):', profileErr)
            }

            loginWithToken(token, profileData)

            const container = document.querySelector('.register-container')
            if (container) {
                container.style.transition = 'opacity 400ms ease-out'
                container.style.opacity = '0'
            }

            setTimeout(() => {
                navigate('/registration-success', {
                    state: {
                        userData: profileData,
                        isNewUser: true  // ← New user registration
                    }
                })
            }, 400)
        } catch (err) {
            localStorage.removeItem('gmc_token')
            setError(extractError(err))
        } finally {
            if (isMounted.current) setLoading(false)
        }
    }

    const handleGoogleFailure = () => {
        setError('Google authentication was closed or failed to initialize.')
    }

    const passLongEnough = form.password.length >= 8
    const passwordsMatch = confirm.length > 0 && confirm === form.password
    const passwordsValid = passLongEnough && passwordsMatch

    const confirmBorder = matchFeedback === null
        ? null
        : matchFeedback
            ? 'border-emerald-500/40 ring-4 ring-emerald-500/[0.04] focus:border-emerald-500/60'
            : 'border-red-500/40 ring-4 ring-red-500/[0.04] focus:border-red-500/60'

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!passwordsValid) {
            setMatchFeedback(false)
            setError('Passwords do not match. Please check and try again.')
            return
        }

        setLoading(true)
        setError(null)

        try {
            const payload = {
                email: form.email,
                password: form.password,
                first_name: form.firstName,
                last_name: form.lastName,
            }
            const res = await register(payload)
            const token = res?.data?.access_token

            if (!token) {
                setError('Registration failed. Please try again.')
                return
            }

            localStorage.setItem('gmc_token', token)

            let profileData = null
            try {
                const profileRes = await getProfile()
                profileData = profileRes.data
            } catch (profileErr) {
                console.warn('Post-auth profile fetch failed (non-fatal):', profileErr)
            }

            loginWithToken(token, profileData)

            const container = document.querySelector('.register-container')
            if (container) {
                container.style.transition = 'opacity 400ms ease-out'
                container.style.opacity = '0'
            }

            setTimeout(() => {
                navigate('/registration-success', {
                    state: {
                        userData: profileData,
                        isNewUser: true  // ← New user registration
                    }
                })
            }, 400)
        } catch (err) {
            localStorage.removeItem('gmc_token')
            setError(extractError(err))
        } finally {
            if (isMounted.current) setLoading(false)
        }
    }

    const formFade = animateIn ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-100'

    return (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <div className="min-h-screen bg-[#07090e] text-slate-100 font-body antialiased relative overflow-x-hidden flex flex-col selection:bg-brand-amber/30 selection:text-white">

                {/* Premium Background Ambiance */}
                <div className="absolute top-0 right-0 w-[800px] h-[400px] bg-gradient-to-b from-brand-amber/[0.02] to-transparent blur-[130px] pointer-events-none" />
                <div className="absolute bottom-0 left-[20%] w-[600px] h-[400px] bg-indigo-500/[0.03] blur-[150px] pointer-events-none rounded-full" />

                <Navbar />

                <div className="flex flex-col lg:flex-row flex-1 relative z-10 register-container">

                    <BrandPanel animateIn={animateIn} />

                    {/* Form panel */}
                    <div className="flex-1 flex flex-col justify-center px-6 py-16 lg:p-20 relative">

                        {/* Stateless Ephemeral Badge */}
                        <div className="absolute top-6 left-6 lg:left-20 hidden sm:block">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/[0.02] border border-white/[0.05] rounded-full shadow-inner backdrop-blur-md">
                                <div className="w-1 h-1 rounded-full bg-brand-amber shadow-[0_0_6px_#ffb000]" />
                                <span className="text-[10px] font-sans font-medium tracking-wider text-slate-400 uppercase">
                                    In-Memory Isolation Engine Active
                                </span>
                            </div>
                        </div>

                        <div className={'w-full max-w-[440px] mx-auto transition-all duration-700 delay-100 ease-out ' + formFade}>

                            <div className="mb-8 text-center sm:text-left">
                                <h2 className="font-display font-bold text-3xl text-white tracking-tight mb-2">
                                    Create account
                                </h2>
                                <p className="text-sm font-body font-light text-slate-400">
                                    Already have an analytical seat?{' '}
                                    <Link to="/login"
                                        className="text-brand-amber font-medium hover:text-amber-400 ml-1 transition-colors">
                                        Sign in
                                    </Link>
                                </p>
                            </div>

                            {/* ── GMC-Branded Google Sign-In Button (Dark Theme) ── */}
                            <div className="w-full mb-6">
                                <button
                                    type="button"
                                    onClick={triggerGoogleSignIn}
                                    disabled={loading}
                                    className="w-full h-12 bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.08] hover:border-white/[0.15] text-white rounded-lg font-sans font-medium text-sm flex items-center justify-center gap-3 transition-all duration-200 hover:shadow-lg hover:shadow-brand-amber/5 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed group"
                                >
                                    <span className="opacity-90 group-hover:opacity-100 transition-opacity">
                                        <GoogleIcon />
                                    </span>
                                    <span className="text-slate-300 group-hover:text-white transition-colors">
                                        Continue with Google
                                    </span>
                                </button>

                                {/* Hidden GoogleLogin component */}
                                <div id="google-signin-container" className="hidden" style={{ display: 'none !important' }}>
                                    <GoogleLogin
                                        onSuccess={handleGoogleSuccess}
                                        onError={handleGoogleFailure}
                                        ux_mode="popup"
                                        theme="filled_black"
                                        shape="pill"
                                        disabled={loading}
                                        width="400"
                                        text="continue_with"
                                        size="large"
                                    />
                                </div>

                                <style dangerouslySetInnerHTML={{
                                    __html: `
                                    #google-signin-container {
                                        display: none !important;
                                        width: 0 !important;
                                        height: 0 !important;
                                        overflow: hidden !important;
                                        position: absolute !important;
                                        pointer-events: none !important;
                                        opacity: 0 !important;
                                    }
                                    #google-signin-container * {
                                        display: none !important;
                                        width: 0 !important;
                                        height: 0 !important;
                                    }
                                ` }} />
                            </div>

                            {/* Separator Section */}
                            <div className="relative flex py-2 items-center mb-6 select-none">
                                <div className="flex-grow border-t border-white/[0.05]"></div>
                                <span className="flex-shrink mx-4 text-[10px] font-sans font-medium text-slate-500 uppercase tracking-[0.18em]">or continue with email</span>
                                <div className="flex-grow border-t border-white/[0.05]"></div>
                            </div>

                            <form onSubmit={handleSubmit} noValidate>

                                <div className="flex flex-col sm:flex-row sm:gap-4">
                                    <FloatingInput
                                        label="First Name" id="firstName" type="text"
                                        value={form.firstName} onChange={handleChange}
                                        placeholder="Jane" disabled={loading}
                                    />
                                    <FloatingInput
                                        label="Last Name" id="lastName" type="text"
                                        value={form.lastName} onChange={handleChange}
                                        placeholder="Smith" disabled={loading}
                                    />
                                </div>

                                <FloatingInput
                                    label="Email Address" id="email" type="email"
                                    value={form.email} onChange={handleChange}
                                    placeholder="jane@firm.com" disabled={loading}
                                />

                                <FloatingInput
                                    label="Password" id="password"
                                    type={showPass ? 'text' : 'password'}
                                    value={form.password}
                                    onChange={handlePasswordChange}
                                    placeholder="Minimum 8 characters"
                                    minLength={8} showToggle disabled={loading}
                                    onToggle={() => setShowPass((v) => !v)}
                                    visible={showPass}
                                />

                                <FloatingInput
                                    label="Confirm Password" id="confirmPassword"
                                    type={showConf ? 'text' : 'password'}
                                    value={confirm}
                                    onChange={handleConfirmChange}
                                    placeholder="Repeat your password"
                                    showToggle={!passwordsMatch}
                                    onToggle={() => setShowConf((v) => !v)}
                                    visible={showConf}
                                    rightAccessory={passwordsMatch ? (
                                        <div className="w-4 h-4 rounded-full bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                                                stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        </div>
                                    ) : null}
                                    borderClass={confirmBorder}
                                    disabled={loading}
                                />

                                {matchFeedback === false && (
                                    <p className="-mt-3 mb-4 ml-1 text-xs font-body font-light text-red-400">
                                        Passwords do not match.
                                    </p>
                                )}

                                {error && (
                                    <div className="mb-5 border border-red-500/20 bg-red-500/[0.06] backdrop-blur-sm rounded-lg px-4 py-3.5">
                                        <div className="flex gap-2.5 items-start">
                                            <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                                            <p className="text-red-300/90 text-xs leading-normal font-light font-body">{error}</p>
                                        </div>
                                    </div>
                                )}

                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        disabled={loading || !passwordsValid}
                                        className={
                                            'w-full h-12 font-sans font-medium text-sm rounded-lg ' +
                                            'transition-all duration-200 flex items-center justify-center gap-2 bg-white text-[#07090e] shadow-xl ' +
                                            (loading || !passwordsValid
                                                ? 'opacity-40 cursor-not-allowed'
                                                : 'hover:bg-slate-200 active:scale-[0.99]')
                                        }
                                    >
                                        {loading ? (
                                            <>
                                                <svg className="animate-spin text-[#07090e]" width="14" height="14"
                                                    viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" strokeWidth="3">
                                                    <circle cx="12" cy="12" r="10"
                                                        strokeDasharray="32" strokeDashoffset="16" />
                                                </svg>
                                                <span>Creating your account...</span>
                                            </>
                                        ) : (
                                            <span>Create Account</span>
                                        )}
                                    </button>

                                    {!passwordsValid && confirm.length > 0 && !loading && (
                                        <p className="mt-3 text-center text-xs font-body font-light text-slate-500">
                                            {!passwordsMatch && confirm.length > 0
                                                ? 'Passwords do not match'
                                                : 'Password must be at least 8 characters'}
                                        </p>
                                    )}
                                </div>

                            </form>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        </GoogleOAuthProvider>
    )
}